import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api/client";

const FAV_KEY = ["favorites"] as const;

type ToggleVars = { secid: string; nextOn: boolean };

// Состояние избранного — одно множество secid'ов в кеше TanStack Query.
// toggle делает оптимистичный update + откат при ошибке сети.
//
// Важно: TanStack Query v5 запускает onMutate ДО mutationFn, поэтому решение
// «add vs remove» принимается в toggle() по pre-state и приходит явным флагом
// в variables. Если читать .has(secid) внутри mutationFn — там уже
// оптимистично-обновлённый кэш, и API-вызов будет инвертирован.
export function useFavorites() {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: FAV_KEY,
    queryFn: async ({ signal }) => {
      const { data, error } = await api.GET("/api/favorites", { signal });
      if (error || !data) throw new Error(String(error ?? "no data"));
      return new Set(data.map((f) => f.secid));
    },
    staleTime: 5 * 60_000,
  });

  const mutation = useMutation({
    mutationFn: async ({ secid, nextOn }: ToggleVars) => {
      const result = nextOn
        ? await api.POST("/api/favorites/{secid}", {
            params: { path: { secid } },
            body: { note: null },
          })
        : await api.DELETE("/api/favorites/{secid}", {
            params: { path: { secid } },
          });
      if (result.error) throw new Error(String(result.error));
    },
    onMutate: async ({ secid, nextOn }: ToggleVars) => {
      await qc.cancelQueries({ queryKey: FAV_KEY });
      const prev = qc.getQueryData<Set<string>>(FAV_KEY);
      qc.setQueryData<Set<string>>(FAV_KEY, (old) => {
        const next = new Set(old);
        if (nextOn) next.add(secid);
        else next.delete(secid);
        return next;
      });
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(FAV_KEY, ctx.prev);
    },
  });

  return {
    favorites: query.data ?? new Set<string>(),
    toggle: (secid: string) => {
      const cur = qc.getQueryData<Set<string>>(FAV_KEY) ?? new Set<string>();
      mutation.mutate({ secid, nextOn: !cur.has(secid) });
    },
  };
}
